<template>
  <cui-filter-wrapper
    class="justify-end mb-0"
    :show-clear-button="showClearButton"
    data-testid="user-filter-wrapper"
    @clear-filters-clicked="emit('clear')"
  >
    <slot name="prepend" />

    <v-text-field
      v-if="!isVertical"
      v-model="searchModel"
      density="compact"
      variant="outlined"
      background-color="white"
      :placeholder="$t('general.search')"
      :aria-label="$t('general.search')"
      class="mr-1"
      :class="['search-field', { 'filter-active': !!searchModel?.length }]"
      prepend-inner-icon="$search"
      hide-details
      clearable
      persistent-clear
      clear-icon="$x"
      data-testid="user-filter-search-field"
    />

    <slot name="after-search" />

    <v-select
      v-model="cloudServicesModel"
      :items="services"
      :disabled="disabled"
      class="cui-filter-item mr-1"
      :class="{ 'cui-filter-active': !!cloudServicesModel?.length }"
      :placeholder="$t('general.cloudApps')"
      density="compact"
      variant="outlined"
      multiple
      rounded
      hide-details
      background-color="white"
      return-object
      data-testid="user-filter-cloud-apps-select"
    >
      <template #selection="{ index }">
        <span v-if="index === 0" class="body2">{{ $t("general.cloudApps") }}</span>
      </template>
      <template #label>
        <span
          v-if="cloudServicesModel?.length"
          class="filter-label"
          data-testid="user-filter-cloud-apps-label-count"
        >
          {{ cloudServicesModel.length }}
        </span>
      </template>
      <template #item="{ item, props: itemProps }">
        <v-list-item v-bind="itemProps" :title="$t(`services.${item.raw}`)">
          <template #prepend="{ isActive }">
            <v-list-item-action start>
              <v-checkbox-btn density="compact" :model-value="isActive" />
            </v-list-item-action>
          </template>
        </v-list-item>
      </template>
    </v-select>

    <v-select
      v-if="!isVertical"
      v-model="suspendedModel"
      :items="suspendedFilterItems"
      :disabled="disabled"
      class="cui-filter-item mr-1"
      :class="{ 'cui-filter-active': suspendedModel !== null }"
      :placeholder="$t('general.status')"
      density="compact"
      variant="outlined"
      rounded
      hide-details
      background-color="white"
      data-testid="user-filter-suspended-select"
    >
      <template #selection="{ item }">
        <span class="body2">{{ $t(`mspUsersPage.filters.statusFilter.${item.value}`) }}</span>
      </template>
      <template #item="{ item, props }">
        <v-list-item
          v-bind="props"
          :title="$t(`mspUsersPage.filters.statusFilter.${item.value}`)"
        ></v-list-item>
      </template>
    </v-select>

    <slot name="append" />
  </cui-filter-wrapper>
</template>

<script setup lang="ts" generic="T extends BaseUserFilters">
import { computed } from "vue";
import { CuiFilterWrapper } from "@coronet/cui";
import type { Service } from "@/constants/cloud-apps.ts";
import type { BaseUserFilters } from "@/_store/filters.module.ts";
import { UsersSuspendedFilterItem } from "@/constants/users.ts";

const suspendedFilterItems = [UsersSuspendedFilterItem.SUSPENDED, UsersSuspendedFilterItem.ACTIVE];

const props = withDefaults(
  defineProps<{
    modelValue: T;
    services: Service[];
    showClearButton?: boolean;
    isVertical?: boolean;
    disabled?: boolean;
  }>(),
  {
    showClearButton: true,
    isVertical: false,
    disabled: false,
  }
);

const emit = defineEmits(["update:modelValue", "clear"]);

const cloudServicesModel = computed({
  get: () => props.modelValue?.cloudServices ?? [],
  set: (value) => {
    emit("update:modelValue", {
      ...props.modelValue,
      cloudServices: value,
    });
  },
});

const searchModel = computed({
  get: () => props.modelValue?.search ?? "",
  set: (value) => {
    emit("update:modelValue", {
      ...props.modelValue,
      search: value,
    });
  },
});

const suspendedModel = computed({
  get: () => props.modelValue?.suspended ?? null,
  set: (value) => {
    emit("update:modelValue", {
      ...props.modelValue,
      suspended: value,
    });
  },
});
</script>
